import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import type { CartItem } from '../page';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  onRemove: (id: string) => void;
  onQuantityChange: (id: string, qty: number) => void;
  paymentLink: string;
}

export const CartDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  items,
  onRemove,
  onQuantityChange,
  paymentLink,
}) => {
  const { user } = useAuth();
  const total = items.reduce((sum, item) => sum + item.priceNum * item.quantity, 0);

  const handleCheckout = () => {
    window.open(paymentLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-white z-50 shadow-2xl flex flex-col transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-shopping-bag-line text-gray-900 text-xl"></i>
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Your Cart</h2>
              <p className="text-xs text-gray-400">{items.length} item{items.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-600 text-lg"></i>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
                <i className="ri-shopping-bag-line text-gray-400 text-2xl"></i>
              </div>
              <p className="text-gray-500 font-medium">Your cart is empty</p>
              <p className="text-gray-400 text-sm mt-1">Browse the store to add items</p>
              <button
                onClick={onClose}
                className="mt-4 text-gray-900 font-semibold text-sm underline cursor-pointer"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map(item => (
                <div key={item.cartId} className="flex gap-3 py-3 border-b border-gray-50">
                  <div className={`flex-shrink-0 ${item.type === 'book' ? 'w-12 h-16' : 'w-14 h-14'}`}>
                    <img
                      src={item.image}
                      alt={item.title}
                      className={`w-full h-full object-cover ${item.type === 'book' ? 'rounded' : 'rounded-xl'}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug mb-1">{item.title}</p>
                    <p className="text-xs text-gray-400 mb-2">{item.type === 'book' ? 'Book' : 'App'}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => onQuantityChange(item.cartId, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer text-gray-600"
                        >
                          <i className="ri-subtract-line text-xs"></i>
                        </button>
                        <span className="w-8 text-center text-sm font-medium text-gray-900">{item.quantity}</span>
                        <button
                          onClick={() => onQuantityChange(item.cartId, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer text-gray-600"
                        >
                          <i className="ri-add-line text-xs"></i>
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900">
                          {item.priceNum === 0 ? 'Free' : `$${(item.priceNum * item.quantity).toFixed(2)}`}
                        </span>
                        <button
                          onClick={() => onRemove(item.cartId)}
                          className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600">Subtotal</span>
              <span className="text-xl font-bold text-gray-900">${total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-gray-400 mb-4 flex items-center gap-1">
              <i className="ri-shield-check-line text-green-500"></i>
              Secure checkout powered by GoDaddy Payments
            </p>
            <button
              onClick={handleCheckout}
              className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-bold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              Proceed to Checkout
            </button>
            {items.some(i => i.downloadUrl) && (
              <Link
                to="/download"
                onClick={onClose}
                className="mt-3 w-full flex items-center justify-center gap-2 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-xl py-2.5 hover:bg-green-100 transition-colors cursor-pointer"
              >
                <i className="ri-download-2-line text-sm"></i>
                Already purchased? Download your PDF
              </Link>
            )}
            {!user && (
              <p className="text-center text-xs text-gray-400 mt-3">
                <a href="/login" className="underline hover:text-gray-600 transition-colors">Sign in</a> to save your order history
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
};
